// Translations (English default, French) and locale-aware formatting.
import { en } from "../i18n/en";
import { fr } from "../i18n/fr";
import { store } from "./ui";

export type Locale = "en" | "fr";

function flat(obj: any, prefix = "", out: Record<string, string> = {}) {
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (typeof v === "string") out[key] = v;
    else flat(v, key, out);
  }
  return out;
}

const DICTS: Record<Locale, Record<string, string>> = { en: flat(en), fr: flat(fr) };

export const i18n = store({ locale: "en" as Locale, enabled: ["en", "fr"] as Locale[] });

const STORAGE_KEY = "tw_lang";

export function initLocale(defaultLocale: Locale, enabled: Locale[]) {
  i18n.enabled = enabled;
  let saved: string | null = null;
  try {
    saved = localStorage.getItem(STORAGE_KEY);
  } catch {
    /* storage unavailable */
  }
  const pick = saved && enabled.includes(saved as Locale) ? (saved as Locale) : defaultLocale;
  applyLocale(pick);
}

export function applyLocale(l: Locale) {
  if (!i18n.enabled.includes(l)) l = i18n.enabled[0] ?? "en";
  i18n.locale = l;
  document.documentElement.lang = l === "fr" ? "fr-CA" : "en-CA";
  try {
    localStorage.setItem(STORAGE_KEY, l);
  } catch {
    /* storage unavailable */
  }
  i18n.set({});
}

const plural = { en: new Intl.PluralRules("en-CA"), fr: new Intl.PluralRules("fr-CA") };

/** Looks up a key; with { count } it picks the plural form (key_one / key_other). */
export function t(key: string, vars?: Record<string, string | number | null | undefined>): string {
  const l = i18n.locale;
  let k = key;
  if (vars && typeof vars.count === "number") {
    const form = plural[l].select(vars.count);
    if (DICTS[l][`${key}_${form}`] ?? DICTS.en[`${key}_${form}`]) k = `${key}_${form}`;
    else if (DICTS[l][`${key}_other`]) k = `${key}_other`;
  }
  let s = DICTS[l][k] ?? DICTS.en[k];
  if (s === undefined) {
    if (location.hostname === "localhost") console.warn("missing translation", key);
    return key;
  }
  if (vars) s = s.replace(/\{(\w+)\}/g, (_, n) => (vars[n] === undefined || vars[n] === null ? "" : String(vars[n])));
  return s;
}

/** Has a translation for this key (used for optional texts such as error codes). */
export function has(key: string) {
  return key in DICTS[i18n.locale] || key in DICTS.en;
}

/** Picks the current language from a {en, fr} object stored in the database. */
export function tl(o: { en?: string; fr?: string } | null | undefined): string {
  if (!o) return "";
  return o[i18n.locale] || o.en || o.fr || "";
}

const tag = () => (i18n.locale === "fr" ? "fr-CA" : "en-CA");

export const fmt = {
  money(cents: number | null | undefined, opts: { whole?: boolean } = {}) {
    if (cents === null || cents === undefined) return "—";
    const whole = opts.whole ?? cents % 100 === 0;
    return new Intl.NumberFormat(tag(), {
      style: "currency", currency: "CAD", currencyDisplay: "narrowSymbol",
      minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2,
    }).format(cents / 100);
  },
  number(n: number, digits = 0) {
    return new Intl.NumberFormat(tag(), { maximumFractionDigits: digits }).format(n);
  },
  percent(n: number) {
    return new Intl.NumberFormat(tag(), { style: "percent", maximumFractionDigits: 2 }).format(n / 100);
  },
  date(d: string | Date | null | undefined, style: "short" | "medium" | "long" | "weekday" = "medium") {
    if (!d) return "—";
    const date = typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + "T12:00:00") : new Date(d);
    const o: Intl.DateTimeFormatOptions =
      style === "short" ? { month: "short", day: "numeric" }
      : style === "long" ? { weekday: "long", month: "long", day: "numeric", year: "numeric" }
      : style === "weekday" ? { weekday: "short", month: "short", day: "numeric" }
      : { month: "short", day: "numeric", year: "numeric" };
    return new Intl.DateTimeFormat(tag(), { ...o, timeZone: "America/Vancouver" }).format(date);
  },
  time(d: string | Date | null | undefined) {
    if (!d) return "—";
    return new Intl.DateTimeFormat(tag(), { hour: "numeric", minute: "2-digit", timeZone: "America/Vancouver" }).format(new Date(d));
  },
  dateTime(d: string | Date | null | undefined) {
    if (!d) return "—";
    return new Intl.DateTimeFormat(tag(), {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/Vancouver",
    }).format(new Date(d));
  },
  relative(d: string | Date) {
    const diff = (new Date(d).getTime() - Date.now()) / 1000;
    const rtf = new Intl.RelativeTimeFormat(tag(), { numeric: "auto" });
    const abs = Math.abs(diff);
    if (abs < 60) return rtf.format(Math.round(diff), "second");
    if (abs < 3600) return rtf.format(Math.round(diff / 60), "minute");
    if (abs < 86400) return rtf.format(Math.round(diff / 3600), "hour");
    return rtf.format(Math.round(diff / 86400), "day");
  },
  bytes(n: number) {
    const u = ["B", "KB", "MB", "GB"];
    let i = 0;
    while (n >= 1024 && i < u.length - 1) {
      n /= 1024;
      i++;
    }
    return `${fmt.number(n, i ? 1 : 0)} ${u[i]}`;
  },
};

/** Human message for an API error code, falling back to a generic one. */
export function errorText(e: any): string {
  const code = e?.code ?? "server_error";
  if (code === "validation" && e?.details?.field) {
    const rule = e.details.rule;
    return t("errors.validation", { field: has(`fields.${e.details.field}`) ? t(`fields.${e.details.field}`) : e.details.field, rule: has(`rules.${rule}`) ? t(`rules.${rule}`) : rule });
  }
  return has(`errors.${code}`) ? t(`errors.${code}`, e?.details ?? {}) : t("errors.server_error");
}
